/**
 * Admin OAuth2 Flow Hook
 * Manages OAuth2 authorization flow initiation for admin authentication
 */

import { useState } from "react";
import {
  adminOAuth2Service,
  oauth2SessionStorage,
  isCryptoSupported,
} from "@/lib/oauth2";
import type { OAuth2SessionData } from "@/types/oauth";

export interface UseAdminOAuth2Return {
  initiateOAuth: (provider: string, redirectUri: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Hook for initiating OAuth2 authorization flow for admin authentication
 *
 * @example
 * ```tsx
 * function LoginPage() {
 *   const { initiateOAuth, isLoading, error } = useAdminOAuth2();
 *
 *   const handleOAuthLogin = () => {
 *     initiateOAuth('tpos', `${window.location.origin}/auth/oauth/callback`);
 *   };
 *
 *   return (
 *     <Button onClick={handleOAuthLogin} disabled={isLoading}>
 *       {isLoading ? 'Redirecting...' : 'Sign in with TPOS'}
 *     </Button>
 *   );
 * }
 * ```
 */
export const useAdminOAuth2 = (): UseAdminOAuth2Return => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initiateOAuth = async (provider: string, redirectUri: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // 1. Validate browser capabilities
      if (!isCryptoSupported()) {
        throw new Error(
          "Your browser does not support required security features for OAuth2",
        );
      }

      // 2. Call backend to initiate OAuth2 flow
      console.log(
        "[AdminOAuth2] Calling backend /api/v1/admin/auth/oauth2/initiate...",
      );
      const response = await adminOAuth2Service.initiate({
        provider,
        redirectUri,
      });

      console.log("[AdminOAuth2] Backend response received");
      console.log(
        "[AdminOAuth2] Authorization URL:",
        response.authorizationUrl,
      );

      // 3. Store OAuth2 session data securely in localStorage
      const sessionData: OAuth2SessionData = {
        provider,
        state: response.state,
        codeVerifier: response.codeVerifier,
        timestamp: Date.now(),
        redirectUri,
      };

      oauth2SessionStorage.save(sessionData);
      console.log("[AdminOAuth2] Session data saved to localStorage");

      // 4. Redirect to authorization endpoint
      console.log("[AdminOAuth2] Redirecting to authorization URL...");
      window.location.href = response.authorizationUrl;
    } catch (err) {
      console.error("[AdminOAuth2] Initiation failed:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to initiate OAuth2 flow";
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const clearError = () => setError(null);

  return { initiateOAuth, isLoading, error, clearError };
};
